import { useState } from 'react';
import { Accordion, AccordionItem, Button, Divider, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Select, SelectItem } from '@nextui-org/react';
import { IconCaretLeftFilled, IconPlus, IconTrash, IconX } from '@tabler/icons-react';
import Render from '../../../../components/render';
import { useIndicators } from '../../../../hooks/use-indicators';

const MODAL = {
  NONE: 0,
  DELETE: 1,
}

const TabIndicators = () => {
  const hook = useIndicators();
  const [selectedItem, setSelectedItem] = useState();
  const [modal, setModal] = useState(MODAL.NONE);

  const selectItem = (modal, item = undefined) => {
    setModal(modal);
    setSelectedItem(item);
  }

  return (
    <div className="w-full flex justify-center items-start gap-4">
      <ConfirmDeleteModal
        item={selectedItem}
        isOpen={modal === MODAL.DELETE && selectedItem}
        isLoading={hook.isLoading}
        onClose={() => selectItem(MODAL.NONE)}
        onConfirm={() => {
          hook.removeFromBD(selectedItem);
          selectItem(MODAL.NONE);
        }}
      />

      <div className="bg-white p-4 rounded-md shadow-md mt-8 w-full max-w-2xl flex flex-col">
        <div className="flex gap-4">
          <Select
            label="Año escolar"
            labelPlacement="outside"
            placeholder="Seleccionar"
            variant="bordered"
            radius="sm"
            className="max-w-xs"
            tabIndex="1"
            disallowEmptySelection={true}
            selectedKeys={!!hook.form.school_year_id ? [hook.form.school_year_id] : []}
            onSelectionChange={v => hook.onChange(v.currentKey, 'school_year_id')}
          >
            {hook.schoolYears.map((item) => (
              <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
            ))}
          </Select>

          <Select
            label="Grado"
            labelPlacement="outside"
            placeholder="Seleccionar"
            variant="bordered"
            radius="sm"
            className="max-w-xs"
            tabIndex="1"
            disallowEmptySelection={true}
            selectedKeys={!!hook.form.school_grade_id ? [hook.form.school_grade_id] : []}
            onSelectionChange={v => hook.onChange(v.currentKey, 'school_grade_id')}
          >
            {hook.grades.map((item) => (
              <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
            ))}
          </Select>
        </div>

        <Divider className="mt-4 mb-2" />
        <p className="font-semibold text-lg">Materia</p>

        <div className="flex justify-start items-start gap-4 mt-2">
          <Select
            label="Código"
            labelPlacement="outside"
            placeholder="Seleccionar"
            variant="bordered"
            radius="sm"
            className="max-w-40"
            tabIndex="1"
            disallowEmptySelection={true}
            selectedKeys={!!hook.form.subject_id ? [hook.form.subject_id] : []}
            onSelectionChange={v => hook.onChange(v.currentKey, 'subject_id')}
          >
            {hook.subjects.map((item) => (
              <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
            ))}
          </Select>

          <Input
            label="Nombre de materia"
            labelPlacement="outside"
            placeholder=" "
            variant="bordered"
            radius="sm"
            className="flex-1 max-w-xs"
            value={hook.form.subject_name}
            readOnly
          />
        </div>

        <Divider className="mt-4 mb-2" />

        <div className="flex justify-between items-start">
          <p className="font-semibold text-lg">Indicadores</p>
          <Button
            className="pl-2 bg-secondary text-white font-semibold text-sm"
            radius="sm"
            size="sm"
            onPress={hook.add}
            startContent={(
              <Render condition={!hook.isLoading}>
                <IconPlus size={18} />
              </Render>
            )}
            isLoading={hook.isLoading}
          >
            Añadir
          </Button>
        </div>

        <div className="flex-1">
          <div className="mt-2 grid grid-cols-3 gap-4">
            {hook.indicators.map((item, index) => (
              <Input
                key={index}
                tabIndex="1"
                variant="bordered"
                radius="sm"
                classNames={{ inputWrapper: 'pr-0' }}
                placeholder={item._description}
                value={item.description}
                onValueChange={v => hook.onChangeIndicator(v, 'description', index)}
                endContent={(
                  <Render condition={!!index}>
                    <Button isIconOnly size="sm" variant="light" title="Eliminar" onPress={() => hook.remove(index)}>
                      <IconX color="gray" />
                    </Button>
                  </Render>
                )}
              />
            ))}
          </div>

          <Render condition={!hook.indicators.length}>
            <p className="text-center text-gray text-sm whitespace-pre py-4">Agregue un indicador para continuar</p>
          </Render>
        </div>

        <div className="flex justify-center mt-4">
          <Button
            isLoading={hook.isLoading}
            isDisabled={hook.isLoading}
            className="mt-4 bg-green text-white font-semibold"
            onPress={hook.submit}
          >
            Guardar
          </Button>
        </div>
      </div>

      <div className="bg-white p-4 rounded-md shadow-md mt-8 w-full max-w-xl">
        <p className="font-semibold text-sm">Indicadores configurados { hook.form.school_year_id ? '| ' + hook.form.school_year_name : '' }</p>
        <Divider className="my-2" />

        <Render condition={hook.form.school_year_id}>
          <p className="font-semibold text-sm text-gray mb-2">Grado</p>
          <div>
            <Accordion selectionMode="multiple" showDivider={false} className="px-0">
              {hook.data?.grouped?.map((grade, gradeIdx) => (
                <AccordionItem
                  key={gradeIdx + 1}
                  aria-label={grade.name}
                  title={grade.name}
                  indicator={<CustomCaret />}
                  classNames={{
                    base: 'mb-2 p-0',
                    trigger: 'border-1 border-divider rounded-lg p-2 data-[open=true]:bg-blue data-[open=true]:border-primary',
                    title: 'font-semibold text-lg data-[open=true]:text-white',
                  }}
                >
                  <div className="grid grid-cols-[2fr,3fr] gap-2 bg-[#8E8E8E] px-3 py-1 rounded-md font-semibold text-sm text-white">
                    <span>Materia</span>
                    <span>Indicadores</span>
                  </div>

                  {grade.subjects.map((subject, subjectIdx) => (
                    <div key={`${gradeIdx}-${subjectIdx}`} className="grid grid-cols-[2fr,3fr] gap-2 pl-3 pr-1.5 mb-2 bg-[#F6FBFF]">
                      <p className="font-semibold py-2">{ subject.name }</p>

                      <div className="[&>div]:border-b-1 [&>div]:border-divider text-sm">
                        {subject.indicators.map((indicator, indicatorIdx) => (
                          <div
                            key={`${gradeIdx}-${subjectIdx}-${indicatorIdx}`}
                            className="flex justify-between items-center py-1"
                          >
                            <span>{ indicator.description }</span>
                            <Button
                              isIconOnly
                              size="sm"
                              variant="light"
                              title="Eliminar"
                              onPress={() => selectItem(MODAL.DELETE, indicator)}
                            >
                              <IconTrash size={18} />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  <Render condition={!grade.subjects?.length}>
                    <p className="text-center text-gray text-sm py-4">No hay materias en este grado</p>
                  </Render>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </Render>

        <Render condition={hook.isLoadingData}>
          <div className="flex gap-4 animate-pulse mb-6">
            <div className="bg-gray/25 h-6 rounded-lg flex-1" />
          </div>
          <div className="flex gap-4 animate-pulse mb-6">
            <div className="bg-gray/25 h-6 rounded-lg flex-1" />
          </div>
          <div className="flex gap-4 animate-pulse mb-6">
            <div className="bg-gray/25 h-6 rounded-lg flex-1" />
          </div>
        </Render>

        <Render condition={!hook.form.school_year_id}>
          <p className="text-center text-gray text-sm whitespace-pre py-4">Seleccione un año escolar</p>
        </Render>
      </div>
    </div>
  );
}

const CustomCaret = () => (
  <div className="w-8 h-8 rounded-full bg-[#E6F4FF] data-[open=true]:bg-secondary items-center justify-center flex">
    <IconCaretLeftFilled className="text-secondary data-[open=true]:text-white" />
  </div>
)

const ConfirmDeleteModal = ({ item, isLoading, isOpen, onClose, onConfirm }) => (
  <Modal isOpen={isOpen} onClose={onClose}>
    <ModalContent>
      <ModalHeader className="flex flex-col gap-1">Confirmar</ModalHeader>
      <ModalBody>
        <p className="text-center">¿Realmente desea eliminar este indicador?</p>
        <p className="text-center font-semibold">{ item?.description }</p>
      </ModalBody>
      <ModalFooter>
        <Button variant="light" onPress={onClose} isDisabled={isLoading}>
          Cancelar
        </Button>
        <Button color="danger" variant="flat" onPress={onConfirm} isLoading={isLoading}>
          Aceptar
        </Button>
      </ModalFooter>
    </ModalContent>
  </Modal>
)

export default TabIndicators;
